import { type UnlockModules } from '@courses/types-layer/types/module'

import { BaseService } from '@common/http-layer/service/base.service'

import { useLocation } from '@common/location-layer/composables/useLocation'
import type { SalesData } from '@courses/types-layer/types/checkout'
import type { CompletedLessons } from '@courses/types-layer/types/learner'

export class LearnerApiService extends BaseService {
  constructor() {
    super({
      baseURL: 'REST_API_URLS',
      basePath: '',
      headers: {
        version: '2021-07-28',
      },
    })
  }

  private endpoint(path: string = '', locationId?: string): string {
    const { store: state } = useLocation()
    if (!state.locationId) throw new Error('Location ID is required')
    return `/courses/learners/locations/${locationId ? locationId : state.locationId}/courses${path}`
  }

  async getNextInCompleteLesson(params: { courseId: string; courseLocationId: string; lessonId?: string }) {
    const { data } = await this.get(
      this.endpoint(
        `/${params.courseId}/lessons/sequence?courseId=${params.courseId}${params.lessonId ? `&lessonId=${params.lessonId ?? ''}` : ''}`,
        params.courseLocationId
      ),
      {
        productId: params.courseId,
        locationId: params.courseLocationId,
      }
    )
    return data
  }

  async getNextInCompleteForAllCourses(courseIdToUserId: Record<string, string>): Promise<Record<string, object>> {
    const { data } = await this.post<Record<string, object>>(this.endpoint(`/lessons/sequence/bulk`), {
      courseIdToUserId,
    })
    return data
  }

  async getUnlockedModules(params: { courseId: string; courseLocationId: string }): Promise<UnlockModules> {
    const { data } = await this.get<UnlockModules>(this.endpoint(`/${params.courseId}/unlocked-modules`, params.courseLocationId), {
      locationId: params.courseLocationId,
      productId: params.courseId,
    })
    return data
  }

  async getCompletedLessons(params: { courseId: string; courseLocationId: string }): Promise<CompletedLessons> {
    const { data } = await this.get<CompletedLessons>(this.endpoint(`/${params.courseId}/lessons/completed`, params.courseLocationId), {
      locationId: params.courseLocationId,
      productId: params.courseId,
    })
    return data
  }

  async getSalesData(params: { courseId: string; courseLocationId: string; offerId?: string; source?: string }): Promise<SalesData> {
    const queryParts: string[] = []
    if (params.offerId) queryParts.push(`offerId=${params.offerId}`)
    if (params.source) queryParts.push(`source=${params.source}`)
    const queryString = queryParts.length ? `?${queryParts.join('&')}` : ''
    const { data } = await this.get<SalesData>(this.endpoint(`/${params.courseId}/sales${queryString}`, params.courseLocationId), {
      locationId: params.courseLocationId,
      productId: params.courseId,
    })
    return data
  }
}
